import {
  Box, Card, CardContent, Typography, TextField, Button, IconButton,
  InputAdornment, Grid, Chip, Divider, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Search, Add, Remove, Delete, Print } from '@mui/icons-material';
import { useState, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(+n || 0);

// ── Code 128B SVG Barcode (supports full ASCII 32-127) ─────────────────────
const C128_PATTERNS = [
  '212222','222122','222221','121223','121322','131222','122213','122312', // 0-7
  '132212','221213','221312','231212','112232','122132','122231','113222', // 8-15
  '123122','123221','223211','221132','221231','213212','223112','312131', // 16-23
  '311222','321122','321221','312212','322112','322211','212123','212321', // 24-31
  '232121','111323','131123','131321','112313','132113','132311','211313', // 32-39
  '231113','231311','112133','112331','132131','113123','113321','133121', // 40-47
  '313121','211331','231131','213113','213311','213131','311123','311321', // 48-55
  '331121','312113','312311','332111','314111','221411','431111','111224', // 56-63
  '111422','121124','121421','141122','141221','112214','112412','122114', // 64-71
  '122411','142112','142211','241211','221114','413111','241112','134111', // 72-79
  '111242','121142','121241','114212','124112','124211','411212','421112', // 80-87
  '421211','212141','214121','412121','111143','111341','131141','114113', // 88-95
  '114311','411113','411311','113141','114131','311141','411131',          // 96-102
  '211412','211214','211232',                                              // 103=StartA 104=StartB 105=StartC
];
const C128_STOP    = '2331112';
const C128_START_B = 104;

function encodeCode128B(str) {
  // Filter to supported ASCII range (32-127); replace others with space
  const safe = str.replace(/[^\x20-\x7E]/g, ' ');
  const vals = [C128_START_B];
  let checksum = C128_START_B;
  for (let i = 0; i < safe.length; i++) {
    const v = safe.charCodeAt(i) - 32; // Code B: ASCII 32 → value 0
    vals.push(v);
    checksum += v * (i + 1);
  }
  vals.push(checksum % 103); // Check character
  return vals;
}

function SvgBarcode({ value, moduleWidth = 1.5, barcodeHeight = 40 }) {
  if (!value) return null;
  const symbols = encodeCode128B(String(value));
  const bars = [];
  let x = 0;

  for (const sym of symbols) {
    const pat = C128_PATTERNS[sym];
    if (!pat) continue;
    for (let i = 0; i < pat.length; i++) {
      const w = parseInt(pat[i]) * moduleWidth;
      if (i % 2 === 0) bars.push({ x, w }); // even indices = bars
      x += w;
    }
  }
  // Stop character
  for (let i = 0; i < C128_STOP.length; i++) {
    const w = parseInt(C128_STOP[i]) * moduleWidth;
    if (i % 2 === 0) bars.push({ x, w });
    x += w;
  }

  const totalW = x + 4; // small right quiet zone
  const totalH = barcodeHeight + 10;

  return (
    <svg width={totalW} height={totalH} viewBox={`0 0 ${totalW} ${totalH}`} style={{ maxWidth: '100%', display: 'block' }}>
      {/* Left quiet zone background */}
      <rect x={0} y={0} width={totalW} height={totalH} fill="white" />
      {bars.map((b, i) => (
        <rect key={i} x={b.x + 2} y={2} width={b.w} height={barcodeHeight} fill="#000" />
      ))}
      <text x={totalW / 2} y={totalH - 1} textAnchor="middle" fontSize="7" fontFamily="monospace" fill="#000">
        {String(value)}
      </text>
    </svg>
  );
}

// ── Label sizes ─────────────────────────────────────────────────────────────
const LABEL_SIZES = {
  small:  { w: 140, h: 70,  bh: 32, fs: 8,  pfs: 10 },
  medium: { w: 180, h: 90,  bh: 42, fs: 9,  pfs: 12 },
  large:  { w: 240, h: 110, bh: 52, fs: 10, pfs: 14 },
};

function LabelCard({ item, size = 'medium' }) {
  const s = LABEL_SIZES[size];
  const barcodeVal = item.barcode || item.sku || String(item.id);
  return (
    <Box sx={{
      border: '1px dashed #bbb', borderRadius: 1,
      width: s.w, minHeight: s.h,
      display: 'inline-flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      p: 0.5, m: 0.5, bgcolor: 'white', boxSizing: 'border-box',
      pageBreakInside: 'avoid',
    }}>
      <Typography sx={{ fontSize: s.fs, fontWeight: 700, textAlign: 'center', lineHeight: 1.2, mb: 0.3, maxWidth: s.w - 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.name}
      </Typography>
      <SvgBarcode value={barcodeVal} moduleWidth={1.2} barcodeHeight={s.bh} />
      <Typography sx={{ fontSize: s.pfs, fontWeight: 800, mt: 0.3 }}>
        ₨ {fmt(item.sale_price)}
      </Typography>
      {item.sku && (
        <Typography sx={{ fontSize: 7, color: '#888', lineHeight: 1 }}>{item.sku}</Typography>
      )}
    </Box>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function BarcodeLabelPage() {
  const [search, setSearch]   = useState('');
  const [results, setResults] = useState([]);
  const [labels, setLabels]   = useState([]); // [{...product, qty}]
  const [size, setSize]       = useState('medium');
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const searchProducts = useCallback(async (term) => {
    if (term.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/products?search=${encodeURIComponent(term)}&limit=10`);
      setResults(data.data?.rows || data.data || []);
    } catch { setResults([]); }
  }, []);

  const addLabel = (product) => {
    setLabels(prev => {
      const existing = prev.find(l => l.id === product.id);
      if (existing) return prev.map(l => l.id === product.id ? { ...l, qty: l.qty + 1 } : l);
      return [...prev, { ...product, qty: 1 }];
    });
    setResults([]);
    setSearch('');
  };

  const updateQty = (id, delta) =>
    setLabels(prev => prev.map(l => l.id === id ? { ...l, qty: Math.max(1, l.qty + delta) } : l));

  const allLabels = labels.flatMap(l =>
    Array.from({ length: l.qty }, (_, i) => ({ ...l, _key: `${l.id}-${i}` }))
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Barcode Label Printing</Typography>
          <Typography variant="caption" color="text.secondary">Code 128B — supports all characters including lowercase, symbols</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={`${allLabels.length} label${allLabels.length !== 1 ? 's' : ''}`} color="primary" />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Label Size</InputLabel>
            <Select value={size} label="Label Size" onChange={e => setSize(e.target.value)}>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<Print />} onClick={handlePrint} disabled={!allLabels.length}>
            Print Labels
          </Button>
          <Button variant="outlined" color="error" onClick={() => setLabels([])} disabled={!labels.length}>
            Clear All
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left: search + queue */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Add Products</Typography>
              <TextField
                fullWidth size="small" placeholder="Search by name, SKU or barcode..."
                value={search}
                onChange={e => { setSearch(e.target.value); searchProducts(e.target.value); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />

              {results.length > 0 && (
                <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 240, overflow: 'auto' }}>
                  {results.map(p => (
                    <Box key={p.id}
                      sx={{ px: 1.5, py: 1, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}
                      onClick={() => addLabel(p)}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.sku || p.barcode || `ID: ${p.id}`}</Typography>
                      </Box>
                      <Add color="primary" fontSize="small" />
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Label Queue
                {labels.length > 0 && <Typography component="span" variant="caption" color="text.secondary" ml={1}>({labels.length} products, {allLabels.length} labels)</Typography>}
              </Typography>

              {labels.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Search and add products above</Typography>
              ) : (
                labels.map(l => (
                  <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{l.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{l.sku || l.barcode || `ID:${l.id}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <IconButton size="small" onClick={() => updateQty(l.id, -1)}><Remove fontSize="small" /></IconButton>
                      <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{l.qty}</Typography>
                      <IconButton size="small" onClick={() => updateQty(l.id, 1)}><Add fontSize="small" /></IconButton>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => setLabels(prev => prev.filter(x => x.id !== l.id))}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: preview */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Live Preview — {LABEL_SIZES[size].w}×{LABEL_SIZES[size].h}px labels
              </Typography>
              <Box sx={{ bgcolor: '#e8e8e8', p: 2, borderRadius: 1, minHeight: 240, maxHeight: 600, overflow: 'auto' }}>
                {allLabels.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 6 }}>
                    Labels will appear here
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {allLabels.map(l => <LabelCard key={l._key} item={l} size={size} />)}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hidden print area */}
      <Box sx={{ display: 'none' }}>
        <Box ref={printRef}>
          <style>{`
            @media print {
              @page { size: A4; margin: 8mm; }
              body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          `}</style>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
            {allLabels.map(l => <LabelCard key={l._key} item={l} size={size} />)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
